import {useCallback, useRef} from 'react'

import PropTypes from 'prop-types'

import {useSuiContext} from '@s-ui/react-context'
import {OBJECT_FIT} from '@adv-mt/ui/media-image'

import {ADJACENT_RADIUS, BASE_CLASS, mediaItemShape} from './config.js'
import GalleryControls from './GalleryControls.js'
import GalleryMediaItem from './GalleryMediaItem.js'
import {getMediaItemKey} from './helpers.js'
import useCarouselIndex from './useCarouselIndex.js'

const MobileGallery = ({
  mediaItems,
  alt,
  galleryLabel,
  hasVideo,
  showControls = true,
  videoIndex,
  canPlayInline,
  isModalOpen = false,
  onOpenGallery,
  onVideoButtonClick,
  onVideoPlayingChange
}) => {
  const {i18n} = useSuiContext()
  const videoLabel = i18n.t('COMMON.VIDEO')

  const totalCount = mediaItems.length
  const {currentIndex, handleScroll} = useCarouselIndex()
  const carouselRef = useRef(null)

  const handleOpenAtCurrent = useCallback(
    () => onOpenGallery?.(currentIndex, {isButton: true}),
    [onOpenGallery, currentIndex]
  )

  const handleOpenVideo = useCallback(() => {
    if (currentIndex === videoIndex) {
      onOpenGallery?.(videoIndex, {isButton: true})
      return
    }
    const el = carouselRef.current
    if (!el) return
    el.scrollTo({left: videoIndex * el.clientWidth, behavior: 'smooth'})
  }, [currentIndex, videoIndex, onOpenGallery])

  return (
    <>
      <div
        ref={carouselRef}
        className={`${BASE_CLASS}-carousel`}
        onScroll={handleScroll}
        role="group"
        aria-label={galleryLabel}
      >
        {mediaItems.map((item, i) => (
          <div
            key={getMediaItemKey(item, i)}
            className={`${BASE_CLASS}-slide`}
            role="group"
            aria-roledescription="slide"
            aria-label={`${i + 1} / ${totalCount}`}
          >
            {Math.abs(i - currentIndex) <= ADJACENT_RADIUS && (
              <GalleryMediaItem
                item={item}
                alt={alt}
                index={i}
                objectFit={OBJECT_FIT.CONTAIN}
                isPriority={i === 0}
                isActive={i === currentIndex && !isModalOpen}
                canPlayInline={canPlayInline}
                useVideoOverlay
                onOpenGallery={onOpenGallery}
                onVideoButtonClick={onVideoButtonClick}
                onVideoPlayingChange={onVideoPlayingChange}
              />
            )}
          </div>
        ))}
      </div>

      {showControls && (
        <GalleryControls
          className={`${BASE_CLASS}-controls`}
          aria-live="polite"
          aria-atomic="true"
          counterLabel={`${currentIndex + 1}/${totalCount}`}
          onCounterClick={handleOpenAtCurrent}
          hasVideo={hasVideo}
          videoLabel={videoLabel}
          isVideoIconOnly
          onVideoClick={handleOpenVideo}
        />
      )}
    </>
  )
}

MobileGallery.displayName = 'MobileGallery'

MobileGallery.propTypes = {
  mediaItems: PropTypes.arrayOf(mediaItemShape).isRequired,
  alt: PropTypes.string,
  galleryLabel: PropTypes.string.isRequired,
  hasVideo: PropTypes.bool,
  showControls: PropTypes.bool,
  videoIndex: PropTypes.number,
  canPlayInline: PropTypes.bool,
  isModalOpen: PropTypes.bool,
  onOpenGallery: PropTypes.func,
  onVideoButtonClick: PropTypes.func,
  onVideoPlayingChange: PropTypes.func
}

export default MobileGallery
